import { useDialog } from "primevue/usedialog";
import { useToast } from "primevue/usetoast";
import { Ref, ref } from "vue";

import FileConflictDialog from "@/components/FileConflictDialog.vue";
import type { ArtefactMeta } from "@/lib/artefacts";
import { useArtefactsStore } from "@/lib/artefacts/user";
import { findUniqueFilename } from "@/lib/filename";
import { mime } from "@/utils";

export function useUniqueArtefactManager(
    userArtefactGroupId: Ref<string>,
    artefacts: Ref<ArtefactMeta[]>
) {
    const isUploading = ref(false);
    const artefactsStore = useArtefactsStore();
    const toast = useToast();
    const dialog = useDialog();

    const nameConflicts = (name: string) => {
        return artefacts.value.some(
            (artefact: ArtefactMeta) => artefact.name === name
        );
    };

    const getUniqueFileName = (name: string) =>
        findUniqueFilename(
            name,
            artefacts.value.map((a) => a.name)
        );

    const overwriteExistingFile = async (
        name: string,
        file: File,
        showToastOnSave: boolean,
        onUploadCompleted: (
            artefactIdentity: string,
            name: string
        ) => void | Promise<void> = () => {}
    ) => {
        const artefact = artefacts.value.find((artefact: ArtefactMeta) => {
            return artefact.name === name;
        });
        if (!artefact) {
            toast.add({
                severity: "error",
                detail: "Trying to overwrite artefact, but artefact not found.",
            });
            return;
        }
        isUploading.value = true;
        await artefactsStore.updateArtefactContent(artefact, file, {
            showToast: showToastOnSave,
        });
        isUploading.value = false;

        await onUploadCompleted(artefact.identity, name);
    };

    const saveFileAsNew = async (
        name: string,
        file: File,
        showToastOnSave: boolean,
        onUploadCompleted: (
            artefactIdentity: string,
            name: string
        ) => void | Promise<void> = () => {}
    ) => {
        if (userArtefactGroupId.value === undefined) {
            toast.add({
                severity: "error",
                detail: "No user artefact group id found",
            });
            return;
        }

        if (nameConflicts(name)) {
            toast.add({
                severity: "error",
                detail: "A file with that name already exists",
            });
            return;
        }

        const uniquefile = new File([file], getUniqueFileName(name), {
            type: file.type || mime.getType(file.name) || "",
            endings: "transparent",
        });

        isUploading.value = true;
        const artefact = await artefactsStore.addArtefact(
            userArtefactGroupId.value,
            uniquefile,
            { showToast: showToastOnSave }
        );
        isUploading.value = false;

        await onUploadCompleted(artefact.identity, name);
    };

    /**
     * Let the user choose how to handle a file conflict
     */
    const showFileConflictDialog = (
        fileName: string,
        file: File,
        showToastOnSave: boolean,
        onUploadCompleted: (
            artefactIdentity: string,
            name: string
        ) => void | Promise<void> = () => {},
        onCancel: () => void | Promise<void> = () => {}
    ) => {
        const uniqueName = getUniqueFileName(fileName);
        dialog.open(FileConflictDialog, {
            props: {
                header: `File "${fileName}" already exists, what do you want to do?`,
                closable: false,
                modal: true,
                draggable: false,
            },
            data: {
                uniqueName: uniqueName,
            },
            emits: {
                onOverwrite: () => {
                    overwriteExistingFile(
                        fileName,
                        file,
                        showToastOnSave,
                        onUploadCompleted
                    );
                },
                onSaveAsUnique: () => {
                    saveFileAsNew(
                        uniqueName,
                        file,
                        showToastOnSave,
                        onUploadCompleted
                    );
                },
                onCancel: async () => {
                    await onCancel();
                },
            },
        });
    };

    const checkConflictsAndAddFile = async (
        name: string,
        file: File,
        showToastOnSave: boolean,
        onUploadCompleted: (
            artefactIdentity: string,
            name: string
        ) => void | Promise<void> = () => {},
        onCancel: () => void = () => {}
    ) => {
        if (nameConflicts(name)) {
            showFileConflictDialog(
                name,
                file,
                showToastOnSave,
                onUploadCompleted,
                onCancel
            );
        } else {
            try {
                await saveFileAsNew(
                    name,
                    file,
                    showToastOnSave,
                    onUploadCompleted
                );
            } finally {
                isUploading.value = false;
            }
        }
    };

    const checkConflictsAndAddFiles = async (
        files: FileList | File[],
        showToastOnSave: boolean,
        onUploadCompleted: (
            artefactIdentity: string,
            name: string
        ) => void | Promise<void> = () => {},
        onCancel: () => void = () => {}
    ) => {
        try {
            for (const file of files) {
                try {
                    await checkConflictsAndAddFile(
                        file.name,
                        file,
                        showToastOnSave,
                        onUploadCompleted,
                        onCancel
                    );
                } catch (error) {
                    console.error(`Error uploading ${file.name}:`, error);
                }
            }
        } finally {
            isUploading.value = false;
        }
    };

    return {
        isUploading,
        nameConflicts,
        getUniqueFileName,
        saveFileAsNew,
        overwriteExistingFile,
        checkConflictsAndAddFile,
        checkConflictsAndAddFiles,
    };
}
